import { useOnboarding, useStyling } from '@/store/CedarStore';
import { MultiInputStep, MultiInputItem } from '@/store/onboardingSlice';
import { Input } from '@/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/ui/select';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { itemVariants } from './animationVariants';
import GumloopButton from './GumloopButton';

interface MultiInputStepRendererProps {
	step: MultiInputStep;
	onInput: (values: { [key: string]: string }) => void;
}

const MultiInputStepRenderer: React.FC<MultiInputStepRendererProps> = ({
	step,
	onInput,
}) => {
	const { nextStep, renderStepText } = useOnboarding();
	const { styling } = useStyling();
	const [inputValues, setInputValues] = useState<{ [key: string]: string }>(
		step.value || {}
	);
	const [touchedFields, setTouchedFields] = useState<{
		[key: string]: boolean;
	}>({});
	const [hoverValues, setHoverValues] = useState<{
		[key: string]: number | null;
	}>({});

	const handleInputChange = (id: string, value: string) => {
		setInputValues((prev) => ({
			...prev,
			[id]: value,
		}));
		setTouchedFields((prev) => ({
			...prev,
			[id]: true,
		}));
	};

	const handleRatingHover = (id: string, value: number | null) => {
		setHoverValues((prev) => ({ ...prev, [id]: value }));
	};

	const isFormValid = () => {
		return step.inputs.every((input) => {
			if (!input.required) return true;
			return Boolean(inputValues[input.id]?.trim());
		});
	};

	const getFieldError = (input: MultiInputItem) => {
		if (!input.required) return '';
		if (!touchedFields[input.id]) return '';
		if (!inputValues[input.id]?.trim()) {
			return 'This field is required';
		}
		return '';
	};

	const handleSubmit = () => {
		// Mark all fields as touched when submitting
		const allTouched = step.inputs.reduce(
			(acc, input) => ({
				...acc,
				[input.id]: true,
			}),
			{}
		);
		setTouchedFields(allTouched);

		if (!isFormValid()) return;
		onInput(inputValues);
		nextStep();
	};

	const renderInput = (input: MultiInputItem) => {
		const error = getFieldError(input);

		if (input.type === 'freeform') {
			return (
				<div className=''>
					<Input
						type='text'
						value={inputValues[input.id] || ''}
						onChange={(e) => handleInputChange(input.id, e.target.value)}
						placeholder={input.placeholder}
						className={`w-full`}
					/>
					{error && <p className='mt-1 text-sm text-red-500'>{error}</p>}
				</div>
			);
		}

		if (input.type === 'longText') {
			return (
				<div className=''>
					<textarea
						className='w-full px-3 py-2 border rounded-md focus:outline-none min-h-[100px] text-sm'
						style={{
							backgroundColor: 'var(--input-background-color)',
							color: 'var(--text-color)',
							borderColor: 'var(--input-background-color)',
						}}
						placeholder={input.placeholder}
						value={inputValues[input.id] || ''}
						onChange={(e) => handleInputChange(input.id, e.target.value)}
					/>
					{error && <p className='mt-1 text-sm text-red-500'>{error}</p>}
				</div>
			);
		}

		if (input.type === 'rating') {
			const ratingValue = inputValues[input.id]
				? parseInt(inputValues[input.id], 10)
				: undefined;
			const currentHoverValue = hoverValues[input.id] || null;

			return (
				<div className='space-y-2'>
					<div
						className='flex w-full rounded-md overflow-hidden'
						style={{
							backgroundColor: 'var(--input-background-color)',
						}}>
						{Array.from({ length: input.max - input.min + 1 }, (_, i) => {
							const value = i + input.min;
							return (
								<button
									key={value}
									type='button'
									className={`
										relative flex-1 py-2 font-medium text-sm border-r last:border-r-0 border-gray-200 transition-colors focus:outline-none
									`}
									style={{
										borderColor: 'var(--input-background-color)',
										color: 'var(--description-color)',
										...(ratingValue !== undefined && value <= ratingValue
											? {
													color: 'var(--text-color)',
													backgroundColor: `${styling?.color}80`,
												}
											: {}),
										...(currentHoverValue !== null && value <= currentHoverValue
											? {
													backgroundColor: `${styling?.color}40`,
												}
											: {}),
									}}
									onClick={() => handleInputChange(input.id, value.toString())}
									onMouseEnter={() => handleRatingHover(input.id, value)}
									onMouseLeave={() => handleRatingHover(input.id, null)}
									aria-label={`Rate ${value} out of ${input.max}`}
									tabIndex={0}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											handleInputChange(input.id, value.toString());
											e.preventDefault();
										}
									}}>
									{value}
								</button>
							);
						})}
					</div>

					{/* Labels */}
					{input.labels && (
						<div
							className='flex justify-between text-sm'
							style={{ color: 'var(--description-color)' }}>
							<span>{input.labels.min}</span>
							<span>{input.labels.max}</span>
						</div>
					)}

					{error && <p className='mt-1 text-sm text-red-500'>{error}</p>}
				</div>
			);
		}

		return (
			<div className='space-y-1'>
				<Select
					value={inputValues[input.id] || ''}
					onValueChange={(value: string) => handleInputChange(input.id, value)}>
					<SelectTrigger
						className={`w-full h-12 ${error ? 'border-red-500' : ''}`}>
						<SelectValue>
							{inputValues[input.id] || `Select ${input.label}`}
						</SelectValue>
					</SelectTrigger>
					<SelectContent>
						{input.options.map((option) => (
							<SelectItem key={option} value={option}>
								{option}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				{error && <p className='text-sm text-red-500'>{error}</p>}
			</div>
		);
	};

	return (
		<div className='flex flex-col items-center w-full p-6 gap-2 justify-center h-full text-sm'>
			<motion.h3 className='text-xl font-semibold mt-4' variants={itemVariants}>
				{renderStepText(step.title)}
			</motion.h3>

			{step.description && (
				<motion.p
					className='text-sm mb-5'
					style={{ color: 'var(--description-color)' }}
					variants={itemVariants}>
					{renderStepText(step.description)}
				</motion.p>
			)}

			{step.inputs.map((input) => (
				<motion.div
					className='w-full max-w-md flex flex-col gap-1 mt-2'
					variants={itemVariants}
					key={input.id}>
					<label className='text-sm font-medium block text-left'>
						{input.label}
						{input.required && <span className='text-red-500 ml-1'>*</span>}
					</label>
					{renderInput(input)}
				</motion.div>
			))}

			<GumloopButton
				onClick={handleSubmit}
				text={step.buttonText || 'Continue'}
				disabled={!isFormValid()}
			/>
		</div>
	);
};

export default MultiInputStepRenderer;
